import { memo, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { FormSummaryData, FormSummaryFieldData } from '@/services/api-types';

/**
 * Read-only projection of Custom Form summaries onto the tour stop detail screen.
 * Data comes from `detail.activeFormSummaries` which the backend populates per §2.6 —
 * only fields marked `summary: true` on their schema are included.
 *
 * Display strings (`displayValue`) are pre-formatted by the backend so the screen
 * and the PDF render identically. Fields are grouped by their schema section.
 */
export interface ActiveFormSummariesSectionProps {
  summaries: FormSummaryData[] | null | undefined;
}

interface SectionGroup {
  sectionId: string;
  sectionTitle: string;
  fields: FormSummaryFieldData[];
}

function groupBySection(fields: FormSummaryFieldData[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  const indexById = new Map<string, number>();
  for (const f of fields) {
    const id = f.sectionId ?? '';
    const title = f.sectionTitle ?? '';
    let idx = indexById.get(id);
    if (idx == null) {
      idx = groups.length;
      indexById.set(id, idx);
      groups.push({ sectionId: id, sectionTitle: title, fields: [] });
    }
    groups[idx].fields.push(f);
  }
  return groups;
}

export const ActiveFormSummariesSection = memo(function ActiveFormSummariesSection({
  summaries,
}: ActiveFormSummariesSectionProps) {
  const { theme } = useTheme();
  const list = summaries ?? [];

  const grouped = useMemo(
    () => list.map((s) => ({ summary: s, groups: groupBySection(s.fields) })),
    [list],
  );

  if (list.length === 0) {
    return null;
  }

  return (
    <>
      {grouped.map(({ summary, groups }) => (
        <div
          key={summary.instanceId}
          className={'tsc-panel' + (theme ? ' ' + theme : '')}
        >
          <div className="tsc-panel-header">
            <i className="fas fa-clipboard-list" /> {summary.schemaName}
          </div>
          <div className="tsc-panel-body">
            {groups.map((group, gi) => (
              <div key={group.sectionId || gi} className="tsc-form-group">
                {group.sectionTitle ? (
                  <div className="tsc-form-section-title">{group.sectionTitle}</div>
                ) : null}
                <div className="tsc-detail-grid">
                  {group.fields.map((field) => (
                    <div key={field.key} className="tsc-detail-item">
                      <span className="tsc-detail-label">{field.label}</span>
                      <span className="tsc-detail-value">{field.displayValue || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
});
